<template>
  <div class="login-page">
    <v-container class="fill-height">
      <v-row align="center" justify="center">
        <v-col cols="12" sm="10" md="8" lg="6" xl="4">
          <v-card elevation="24" class="login-card">
            <!-- Header -->
            <v-card-title class="login-header text-center pa-8">
              <div class="w-100">
                <img src="/assets/img/logo-trans.png" alt="Space Coast STEM" class="logo mb-4">
                <h1 class="text-h4 font-weight-bold mb-2">Portal Access</h1>
                <p class="text-subtitle-1 mb-0">
                  {{ activeTab === 'login' ? 'Sign in to access the dashboard' : 'Create a new account' }}
                </p>
              </div>
            </v-card-title>

            <!-- Tabs -->
            <v-tabs v-model="activeTab" bg-color="grey-lighten-4" grow>
              <v-tab value="login">
                <v-icon start>mdi-login</v-icon>
                Login
              </v-tab>
              <v-tab value="signup">
                <v-icon start>mdi-account-plus</v-icon>
                Sign Up
              </v-tab>
            </v-tabs>

            <v-card-text class=" pt-8">
              <v-window v-model="activeTab" class="pa-4">
                <!-- Login Tab -->
                <v-window-item value="login">
                  <v-form @submit.prevent="handleLogin" ref="loginFormRef">
                    <v-text-field
                      v-model="credentials.username"
                      label="Email or Username"
                      variant="outlined"
                      prepend-inner-icon="mdi-account"
                      :rules="[v => !!v || 'Username is required']"
                      required
                      class="mb-3"
                    ></v-text-field>

                    <v-text-field
                      v-model="credentials.password"
                      label="Password"
                      type="password"
                      variant="outlined"
                      prepend-inner-icon="mdi-lock"
                      :rules="[v => !!v || 'Password is required']"
                      required
                      class="mb-3"
                    ></v-text-field>

                    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
                      {{ error }}
                    </v-alert>

                    <v-btn
                      type="submit"
                      color="primary"
                      size="large"
                      block
                      :loading="loading"
                      class="text-none"
                    >
                      <v-icon start>mdi-login</v-icon>
                      Sign In
                    </v-btn>
                  </v-form>
                </v-window-item>

                <!-- Signup Tab -->
                <v-window-item value="signup">
                  <!-- Success Message - Show After Signup -->
                  <div v-if="signupSuccess" class="signup-success">
                    <v-icon size="80" color="success" class="mb-4">mdi-check-circle</v-icon>
                    <h2 class="text-h5 mb-4">Account Created Successfully!</h2>

                    <v-alert type="warning" class="mb-4 text-left">
                      <v-alert-title class="mb-3">
                        <v-icon start>mdi-clock-alert</v-icon>
                        <strong>Approval Required</strong>
                      </v-alert-title>
                      <p class="mb-2">Your account is pending administrator approval. You will not be able to log in until your account has been approved.</p>
                      <p class="mb-3"><strong>What happens next?</strong></p>
                      <ul class="mb-2">
                        <li>Our administrators have been notified of your signup</li>
                        <li>They will review your account information</li>
                        <li>You'll receive an email when your account is approved</li>
                        <li>Once approved, you can log in and start using the portal</li>
                      </ul>
                      <p class="mb-0 text-caption">This typically happens within 24 hours.</p>
                    </v-alert>

                    <v-btn
                      color="primary"
                      size="large"
                      @click="resetSignupForm"
                      class="text-none"
                    >
                      <v-icon start>mdi-arrow-left</v-icon>
                      Back to Login
                    </v-btn>
                  </div>

                  <!-- Signup Form - Hidden After Successful Signup -->
                  <v-form v-else @submit.prevent="handleSignup" ref="signupFormRef">
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="signupData.first_name"
                          label="First Name"
                          variant="outlined"
                          :rules="[v => !!v || 'First name is required']"
                          required
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="signupData.last_name"
                          label="Last Name"
                          variant="outlined"
                          :rules="[v => !!v || 'Last name is required']"
                          required
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <v-text-field
                      v-model="signupData.email"
                      label="Email"
                      type="email"
                      variant="outlined"
                      prepend-inner-icon="mdi-email"
                      :rules="[
                        v => !!v || 'Email is required',
                        v => /.+@.+\..+/.test(v) || 'Email must be valid',
                        validateEmailDomain
                      ]"
                      required
                      class="mb-3"
                      :hint="emailHint"
                      :persistent-hint="!!emailHint"
                    ></v-text-field>

                    <v-select
                      v-model="signupData.school"
                      :items="schoolOptions"
                      label="School"
                      variant="outlined"
                      prepend-inner-icon="mdi-school"
                      :rules="[v => !!v || 'School is required']"
                      required
                      class="mb-3"
                    ></v-select>

                    <v-text-field
                      v-model="signupData.password"
                      label="Password"
                      type="password"
                      variant="outlined"
                      prepend-inner-icon="mdi-lock"
                      :rules="[
                        v => !!v || 'Password is required',
                        v => v.length >= 8 || 'Password must be at least 8 characters'
                      ]"
                      required
                      class="mb-3"
                    ></v-text-field>

                    <v-text-field
                      v-model="signupData.password_confirm"
                      label="Confirm Password"
                      type="password"
                      variant="outlined"
                      prepend-inner-icon="mdi-lock-check"
                      :rules="[
                        v => !!v || 'Please confirm password',
                        v => v === signupData.password || 'Passwords must match'
                      ]"
                      required
                      class="mb-3"
                    ></v-text-field>

                    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
                      {{ error }}
                    </v-alert>

                    <v-btn
                      type="submit"
                      color="primary"
                      size="large"
                      block
                      :loading="loading"
                      class="text-none"
                    >
                      <v-icon start>mdi-account-plus</v-icon>
                      Create Account
                    </v-btn>
                  </v-form>
                </v-window-item>
              </v-window>
            </v-card-text>

            <v-divider></v-divider>

          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useSchools } from '@/composables/useSchools'
import axios from 'axios'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { schools, loadSchools } = useSchools()

const activeTab = ref('login')
const loginFormRef = ref<any>(null)
const signupFormRef = ref<any>(null)

// Load schools on mount and check for signup query param
onMounted(() => {
  loadSchools()
  loadAllowedDomains()

  // Check if signup query parameter is present
  if (route.query.signup === 'true' || route.query.signup === '1') {
    activeTab.value = 'signup'
  }
})

const credentials = ref({
  username: '',
  password: ''
})

const signupData = ref({
  first_name: '',
  last_name: '',
  email: '',
  school: '',
  password: '',
  password_confirm: ''
})

const loading = ref(false)
const error = ref('')
const successMessage = ref('')
const signupSuccess = ref(false)
const allowedDomains = ref<string[]>([])

// Load allowed domains from configuration
const loadAllowedDomains = async () => {
  try {
    const response = await axios.get('/api/v1/configuration')
    allowedDomains.value = response.data.allowed_domains || []
  } catch (err) {
    console.error('Error loading allowed domains:', err)
    allowedDomains.value = []
  }
}

// Email domain validation
const validateEmailDomain = (email: string): boolean | string => {
  if (allowedDomains.value.length === 0) {
    return true // No restrictions if no domains configured
  }
  const emailLower = email.toLowerCase()
  for (const domain of allowedDomains.value) {
    if (emailLower.endsWith('@' + domain.toLowerCase())) {
      return true
    }
  }
  const domainList = allowedDomains.value.map(d => '@' + d).join(', ')
  return `Email must end with: ${domainList}`
}

// Hint text for email field
const emailHint = computed(() => {
  if (allowedDomains.value.length === 0) {
    return ''
  }
  if (allowedDomains.value.length === 1) {
    return `Must be a @${allowedDomains.value[0]} email address`
  }
  return `Allowed domains: ${allowedDomains.value.map(d => '@' + d).join(', ')}`
})

const schoolOptions = computed(() => {
  const options = schools.value.map(school => ({
    title: school.name,
    value: school.id
  }))
  return options
})

const resetSignupForm = () => {
  signupSuccess.value = false
  activeTab.value = 'login'
  signupData.value = {
    first_name: '',
    last_name: '',
    email: '',
    school: '',
    password: '',
    password_confirm: ''
  }
  signupFormRef.value?.reset()
}

const handleLogin = async () => {
  const { valid } = await loginFormRef.value.validate()
  if (!valid) return

  loading.value = true
  error.value = ''

  try {
    const response = await authStore.login(credentials.value)

    if (response.status === 200) {
      // Successfully logged in, redirect to portal
      router.push('/')
    } else {
      error.value = 'Invalid username or password'
    }
  } catch (err: any) {
    console.error('Login error:', err)
    if (err.response?.status === 401) {
      error.value = 'Invalid username or password'
    } else if (err.response?.status === 403) {
      error.value = 'Your account is pending approval. Please wait for an administrator to approve your account.'
    } else {
      error.value = 'An error occurred. Please try again.'
    }
  } finally {
    loading.value = false
  }
}

const handleSignup = async () => {
  const { valid } = await signupFormRef.value.validate()
  if (!valid) return

  loading.value = true
  error.value = ''
  successMessage.value = ''

  // Validate passwords match
  if (signupData.value.password !== signupData.value.password_confirm) {
    error.value = 'Passwords do not match'
    loading.value = false
    return
  }

  try {
    const response = await axios.post('/api/v1/auth/signup', signupData.value)

    if (response.data.error) {
      error.value = response.data.error
    } else {
      // Show success state instead of alert
      signupSuccess.value = true
      error.value = ''
    }
  } catch (err: any) {
    console.error('Signup error:', err)
    error.value = err.response?.data?.error || 'An error occurred during signup. Please try again.'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.login-page {
  min-height: 100vh;
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  display: flex;
  align-items: center;
  font-family: 'Inter', sans-serif;
}

.login-card {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3) !important;
}

.login-header {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: white;
}

.login-header h1 {
  font-family: 'Poppins', sans-serif;
  color: white;
}

.login-header p {
  color: rgba(255, 255, 255, 0.9);
}

.logo {
  width: 120px;
  height: 120px;
  display: block;
  margin: 0 auto;
}

.w-100 {
  width: 100%;
}

.signup-success {
  text-align: center;
  padding: 20px;
}

.signup-success h2 {
  font-family: 'Poppins', sans-serif;
  color: #2c4a6b;
}
</style>
